import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import * as pricingPlanService from '../services/pricingPlanService';
import type { PricingPlan, PlanStats, CreatePricingPlanInput, UpdatePricingPlanInput } from '../services/pricingPlanService';

interface PlanFormData {
  name: string;
  internal_key: string;
  description: string;
  price_monthly: string;
  price_annual: string;
  max_users: string;
  max_projects: string;
  trial_duration_days: string;
  features: string;
  not_included: string;
  sort_order: string;
  is_highlighted: boolean;
  highlight_badge: string;
  cta_text: string;
  show_on_pricing_page: boolean;
  role_key: string;
}

const BILLABLE_ROLES = [
  { value: '', label: 'None (no role)' },
  { value: 'company_admin', label: 'Company Admin' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'project_manager_lite', label: 'Project Manager Lite' },
  { value: 'contributor', label: 'Contributor' },
  { value: 'taskboard_user', label: 'Taskboard User' },
  { value: 'stakeholder', label: 'Stakeholder' },
];

const ROLE_LABELS: Record<string, string> = {
  company_admin: 'Company Admin',
  project_manager: 'Project Manager',
  project_manager_lite: 'PM Lite',
  contributor: 'Contributor',
  taskboard_user: 'Taskboard User',
  stakeholder: 'Stakeholder',
};

const emptyForm: PlanFormData = {
  name: '',
  internal_key: '',
  description: '',
  price_monthly: '',
  price_annual: '',
  max_users: '',
  max_projects: '',
  trial_duration_days: '7',
  features: '',
  not_included: '',
  sort_order: '0',
  is_highlighted: false,
  highlight_badge: '',
  cta_text: 'Get Started',
  show_on_pricing_page: true,
  role_key: '',
};

function PricingPlanManagement(): React.ReactElement {
  const { token } = useAuth();
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [stats, setStats] = useState<PlanStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PricingPlan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadPlans = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const [plansData, statsData] = await Promise.all([
        pricingPlanService.getAllPlans(token),
        pricingPlanService.getPlanStats(token)
      ]);
      setPlans(plansData);
      setStats(statsData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const getStatForPlan = (internalKey: string): number => {
    const stat = stats.find(s => s.internal_key === internalKey);
    return stat ? stat.company_count : 0;
  };

  const totalCompanies = stats.reduce((sum, s) => sum + s.company_count, 0);

  const parseJsonArray = (jsonStr: string): string[] => {
    try {
      const parsed = JSON.parse(jsonStr);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const openCreateModal = () => {
    setEditingPlan(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleDuplicate = (plan: PricingPlan) => {
    setEditingPlan(null);
    const features = parseJsonArray(plan.features);
    const notIncluded = parseJsonArray(plan.not_included);
    setFormData({
      name: `${plan.name} (Copy)`,
      internal_key: `${plan.internal_key}_copy`,
      description: plan.description || '',
      price_monthly: plan.price_monthly !== null ? String(plan.price_monthly) : '',
      price_annual: plan.price_annual !== null ? String(plan.price_annual) : '',
      max_users: plan.max_users !== null ? String(plan.max_users) : '',
      max_projects: plan.max_projects !== null ? String(plan.max_projects) : '',
      trial_duration_days: String(plan.trial_duration_days),
      features: features.join('\n'),
      not_included: notIncluded.join('\n'),
      sort_order: String(plan.sort_order),
      is_highlighted: false,
      highlight_badge: '',
      cta_text: plan.cta_text || 'Get Started',
      show_on_pricing_page: false,
      role_key: plan.role_key || '',
    });
    setShowModal(true);
  };

  const openEditModal = (plan: PricingPlan) => {
    setEditingPlan(plan);
    const features = parseJsonArray(plan.features);
    const notIncluded = parseJsonArray(plan.not_included);
    setFormData({
      name: plan.name,
      internal_key: plan.internal_key,
      description: plan.description || '',
      price_monthly: plan.price_monthly !== null ? String(plan.price_monthly) : '',
      price_annual: plan.price_annual !== null ? String(plan.price_annual) : '',
      max_users: plan.max_users !== null ? String(plan.max_users) : '',
      max_projects: plan.max_projects !== null ? String(plan.max_projects) : '',
      trial_duration_days: String(plan.trial_duration_days),
      features: features.join('\n'),
      not_included: notIncluded.join('\n'),
      sort_order: String(plan.sort_order),
      is_highlighted: plan.is_highlighted,
      highlight_badge: plan.highlight_badge || '',
      cta_text: plan.cta_text || 'Get Started',
      show_on_pricing_page: plan.show_on_pricing_page,
      role_key: plan.role_key || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!token) return;
    if (!formData.name || !formData.internal_key) {
      setError('Name and Internal Key are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const featuresArray = formData.features
        .split('\n')
        .map(f => f.trim())
        .filter(f => f.length > 0);
      const notIncludedArray = formData.not_included
        .split('\n')
        .map(f => f.trim())
        .filter(f => f.length > 0);

      const payload: CreatePricingPlanInput & UpdatePricingPlanInput = {
        name: formData.name,
        internal_key: formData.internal_key,
        description: formData.description || undefined,
        price_monthly: formData.price_monthly ? parseFloat(formData.price_monthly) : null,
        price_annual: formData.price_annual ? parseFloat(formData.price_annual) : null,
        max_users: formData.max_users ? parseInt(formData.max_users) : null,
        max_projects: formData.max_projects ? parseInt(formData.max_projects) : null,
        trial_duration_days: parseInt(formData.trial_duration_days) || 7,
        features: JSON.stringify(featuresArray),
        not_included: JSON.stringify(notIncludedArray),
        sort_order: parseInt(formData.sort_order) || 0,
        is_highlighted: formData.is_highlighted,
        highlight_badge: formData.highlight_badge || undefined,
        cta_text: formData.cta_text || 'Get Started',
        show_on_pricing_page: formData.show_on_pricing_page,
        role_key: formData.role_key || null,
      };

      if (editingPlan) {
        await pricingPlanService.updatePlan(editingPlan.id, payload, token);
      } else {
        await pricingPlanService.createPlan(payload as CreatePricingPlanInput, token);
      }

      setShowModal(false);
      await loadPlans();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save pricing plan');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (plan: PricingPlan) => {
    if (!token) return;
    try {
      if (plan.is_active) {
        await pricingPlanService.deletePlan(plan.id, token);
      } else {
        await pricingPlanService.updatePlan(plan.id, { is_active: true }, token);
      }
      await loadPlans();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update plan status');
    }
  };

  const formatPrice = (price: number | null): string => {
    if (price === null) return 'Custom';
    if (price === 0) return '$0';
    return `$${price.toFixed(2)}`;
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pricing Plans</h1>
            <p className="text-gray-500 mt-1">Manage pricing tiers displayed on the public pricing page</p>
          </div>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium rounded-full hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md"
          >
            Create Plan
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* KPI Cards */}
        {!loading && plans.length > 0 && (
          <div className="mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Total Companies Card */}
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Companies</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{totalCompanies}</p>
              <p className="text-xs text-gray-400 mt-1">across all plans</p>
            </div>
            {/* Per-plan cards */}
            {plans.filter(p => p.is_active).map((plan) => {
              const count = getStatForPlan(plan.internal_key);
              const colors: Record<string, string> = {
                free: 'border-gray-400',
                starter: 'border-blue-500',
                professional: 'border-purple-500',
                enterprise: 'border-amber-500'
              };
              const borderColor = colors[plan.internal_key] || 'border-green-500';
              return (
                <div key={plan.id} className={`bg-white rounded-lg shadow p-4 border-l-4 ${borderColor}`}>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{plan.name}</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {totalCompanies > 0 ? `${Math.round((count / totalCompanies) * 100)}%` : '0%'} of total
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-500">Loading pricing plans...</span>
          </div>
        ) : (
          /* Plans Table */
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Annual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projects</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Public</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Highlighted</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plans.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-6 py-12 text-center text-gray-500">
                      No pricing plans found. Click "Create Plan" to add one.
                    </td>
                  </tr>
                ) : (
                  plans.map((plan) => (
                    <tr key={plan.id} className={!plan.is_active ? 'bg-gray-50 opacity-60' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.sort_order}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">{plan.name}</span>
                          {plan.highlight_badge && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                              {plan.highlight_badge}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {plan.role_key ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700">
                            {ROLE_LABELS[plan.role_key] || plan.role_key}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{plan.internal_key}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatPrice(plan.price_monthly)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatPrice(plan.price_annual)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {plan.max_users !== null ? plan.max_users : 'Unlimited'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {plan.max_projects !== null ? plan.max_projects : 'Unlimited'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          plan.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {plan.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {plan.show_on_pricing_page ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Visible</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">Hidden</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {plan.is_highlighted ? (
                          <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => openEditModal(plan)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDuplicate(plan)}
                          className="text-gray-600 hover:text-gray-900 mr-3"
                        >
                          Duplicate
                        </button>
                        <button
                          onClick={() => handleToggleActive(plan)}
                          className={plan.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                        >
                          {plan.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50" onClick={() => setShowModal(false)}>
            <div
              className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-2xl rounded-lg bg-white mb-10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingPlan ? 'Edit Pricing Plan' : 'Create Pricing Plan'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                {/* Name & Key */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g. Premium"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Internal Key *</label>
                    <input
                      type="text"
                      value={formData.internal_key}
                      onChange={(e) => setFormData({ ...formData, internal_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono"
                      placeholder="e.g. professional"
                    />
                    <p className="text-xs text-gray-400 mt-1">Lowercase, alphanumeric, underscores only</p>
                  </div>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Application Role</label>
                  <select
                    value={formData.role_key}
                    onChange={(e) => setFormData({ ...formData, role_key: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {BILLABLE_ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Links this plan's pricing to users with this role for per-seat billing</p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Short description of this plan"
                  />
                </div>

                {/* Pricing */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price_monthly}
                      onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Leave empty for custom pricing"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Annual Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price_annual}
                      onChange={(e) => setFormData({ ...formData, price_annual: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Leave empty for custom pricing"
                    />
                  </div>
                </div>

                {/* Limits */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Users</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.max_users}
                      onChange={(e) => setFormData({ ...formData, max_users: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Projects</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.max_projects}
                      onChange={(e) => setFormData({ ...formData, max_projects: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.trial_duration_days}
                      onChange={(e) => setFormData({ ...formData, trial_duration_days: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* Features */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Features (one per line)</label>
                  <textarea
                    value={formData.features}
                    onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                    placeholder={"Up to 50 users\nUnlimited projects\nAI requirements generation"}
                  />
                </div>

                {/* Not Included */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Not Included (one per line)</label>
                  <textarea
                    value={formData.not_included}
                    onChange={(e) => setFormData({ ...formData, not_included: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                    placeholder={"Custom AI configurations\nDedicated account manager"}
                  />
                </div>

                {/* Display Options */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                    <input
                      type="text"
                      value={formData.highlight_badge}
                      onChange={(e) => setFormData({ ...formData, highlight_badge: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g. Most Popular"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                    <input
                      type="text"
                      value={formData.cta_text}
                      onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Get Started"
                    />
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="show_on_pricing_page"
                      checked={formData.show_on_pricing_page}
                      onChange={(e) => setFormData({ ...formData, show_on_pricing_page: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="show_on_pricing_page" className="ml-2 text-sm text-gray-700">
                      Show on public pricing page
                    </label>
                    <span className="ml-2 text-xs text-gray-400">(uncheck to hide from visitors, e.g. for internal or friends plans)</span>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_highlighted"
                      checked={formData.is_highlighted}
                      onChange={(e) => setFormData({ ...formData, is_highlighted: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_highlighted" className="ml-2 text-sm text-gray-700">
                      Highlight this plan (recommended/featured styling)
                    </label>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-6 flex justify-end space-x-3 border-t pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium rounded-full hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default PricingPlanManagement;
